/**
 * Auth endpoints — current user, signin/signup/forgot/reset/verify/signout,
 * onboarding wizard state + complete.
 */

import type { CurrentUser } from '../types.ts';
import { ApiError, extractError, get, post } from './_shared.ts';

export function fetchCurrentUser(): Promise<CurrentUser> {
  return get<CurrentUser>('/api/me');
}

/**
 * Phase 2 Finishing Pass Round 3 Item 1 — resolves the workspace owner/admin
 * contact for the "Ask admin" mailto nudge on PermissionDisallowedCard.
 *
 * Resolution: owner first, then first admin alphabetically. `null` returned
 * when no qualifying contact exists (caller is the only privileged user, or
 * no owner/admin has an email on file). SPA hides the button on null.
 */
export interface AdminContact {
  account_user_id: string;
  display_name: string;
  email: string | null;
  role: 'owner' | 'admin';
}

export function fetchAdminContact(): Promise<{ contact: AdminContact | null }> {
  return get<{ contact: AdminContact | null }>('/api/me/admin-contact');
}

// ---- D-168 owner onboarding wizard (/welcome) ----
// State endpoint reports whether the calling account_user has finished the
// wizard yet (NULL = first-time → route to /welcome). Complete endpoint
// flips NULL → now() idempotently. Wizard gating is ROUTING-only per D-160.

export interface OnboardingState {
  onboarding_completed_at: string | null;
  role: 'owner' | 'admin' | 'user';
}

export function fetchOnboardingState(): Promise<OnboardingState> {
  return get<OnboardingState>('/api/onboarding/state');
}

export function completeOnboarding(): Promise<{ ok: true; changed: boolean }> {
  return post('/api/onboarding/complete', {});
}

/**
 * Sub-agent GGGGG (2026-07-29) — Track 3 client-side timeout wrapper.
 *
 * The generic `post()` helper in `_shared.ts` has no timeout: if the
 * Cloudflare Pages Function or the upstream Better Auth server hangs
 * (network stall, DNS blip, IDP unresponsive), the fetch pends
 * indefinitely and the SignIn UI stays parked on "Signing you in…"
 * forever. Ean hit this repeatedly after leaving the tab idle
 * overnight — reload → sign-in submit → infinite spinner, no
 * recovery affordance.
 *
 * Fix: signIn wires its own AbortController; throws a friendly
 * `ApiError` on abort so SignIn.tsx can branch on
 * `err.code === 'signin_timeout'` and auto-recover via
 * resetAndRedirectToSignIn() (hard reload → pristine sign-in form).
 *
 * Sub-agent HHHHH (2026-07-29): tightened 30s → 10s.
 *
 * Sub-agent NO-ABORT (2026-08-13): RELAXED 10s → 90s per Ean's
 * "slow is better than cancel" directive. The 10s belt was firing on
 * cold Neon compute, aborting genuine in-flight signins before the
 * server could respond and leaving the user staring at "That request
 * took too long." Design rules (locked with NO-ABORT push):
 *
 *   Best:   fast completion (2-3s)
 *   Fine:   slow completion + "still working…" cue (5-30s)
 *   Bad:    fast client abort before server completes
 *   Worst:  indefinite spin with no signal
 *
 * 90s is the never-abort-before-that floor. Progressive copy shifts
 * (see useAuthElapsedProgress) tell the user we're still working at
 * 4s / 15s / 45s so the wait feels intentional, not stuck.
 *
 * The server-side Pages Function caps at ~25s upstream (see
 * signin.ts UPSTREAM_TIMEOUT_MS) so in practice a real upstream stall
 * still surfaces server-side (504 envelope) well before this client
 * timer fires. The 90s belt catches network-level TCP-hang cases where
 * the response never leaves the edge.
 */
export const SIGNIN_TIMEOUT_MS = 90_000;

/**
 * Sub-agent NO-ABORT (2026-08-13) — signup client-side belt.
 *
 * Historically signUp() used the generic post() helper with no timeout,
 * so a hung server response would spin indefinitely. The Pages Function
 * caps at ~30s wall-clock, so response bounded there, but a stuck TCP
 * or edge stall could leave the SPA parked forever.
 *
 * 90s ceiling matches SIGNIN_TIMEOUT_MS. Any abort past 90s surfaces
 * a `signup_timeout` code so SignUp.tsx can render the recovery UI
 * (retry + "sign in with your email if it already exists" link)
 * instead of a bare error toast.
 *
 * Note: with the D-185 check + email_taken flow already handling the
 * "orphan neon_auth user from a prior aborted attempt" case idempotently
 * (see signup.ts), a client-side abort at 90s is safe to retry — a
 * subsequent submit either succeeds fresh or returns 409 email_taken +
 * the "Sign in / Reset" callout.
 */
export const SIGNUP_TIMEOUT_MS = 90_000;

/**
 * Sub-agent HHHHH (2026-07-29) — auto-recover primitive.
 *
 * Fire-and-forget /api/signout to nuke any half-set session cookie,
 * clear any lingering sign-in-flow session storage, then hard-reload
 * to /sign-in?_r=<ts>&reset=1 for a pristine sign-in surface. Wins over
 * the Push 100 "retry button" pattern for stuck-session recovery: no
 * user click required, the reload naturally re-boots the SPA in a
 * clean state.
 *
 * Reset count is tracked in sessionStorage so a runaway loop caps at
 * 2 attempts — after that SignIn.tsx surfaces the manual retry
 * callout instead of firing another reset.
 *
 * Query param contract on the reloaded /sign-in:
 *   ?_r=<ts>  — cache-bust, stripped from URL on mount
 *   ?reset=1  — triggers a one-time "Sign-in refreshed" flash
 */
export function resetAndRedirectToSignIn(): void {
  // Increment session-scoped reset counter. Caps auto-recover at 2
  // attempts per session so a persistent server outage doesn't loop
  // the reset flow forever.
  try {
    const prev = Number(
      window.sessionStorage.getItem('olytic:signin:reset_count') ?? '0',
    );
    const next = Number.isFinite(prev) ? prev + 1 : 1;
    window.sessionStorage.setItem('olytic:signin:reset_count', String(next));
  } catch {
    /* SSR / storage-disabled — best-effort */
  }
  // Clear any leftover in-progress marker.
  try {
    window.sessionStorage.removeItem('olytic:signin:started_at');
  } catch {
    /* noop */
  }
  // Sub-agent IIIII (2026-07-29) — Track 5. Preserve the caller's
  // current path as `next=<path>` so the post-sign-in landing goes
  // back to where the user was, not the default /home. If the current
  // URL already carries a `?next=`, honor that (a chained auto-
  // recover shouldn't clobber the wizard-preserved next path). Skip
  // preservation when we're already on /sign-in (avoid recursive
  // ?next=/sign-in loops), when the current path is /home (the
  // default landing anyway), or when the path itself is empty.
  let nextPath: string | null = null;
  try {
    const currentUrl = new URL(window.location.href);
    // Prefer an already-set ?next= if present.
    const existingNext = currentUrl.searchParams.get('next');
    if (existingNext !== null && existingNext !== '') {
      nextPath = existingNext;
    } else {
      const pathWithSearch = `${currentUrl.pathname}${currentUrl.search}`;
      if (
        currentUrl.pathname !== '/sign-in' &&
        currentUrl.pathname !== '/' &&
        currentUrl.pathname !== '/home' &&
        pathWithSearch !== ''
      ) {
        nextPath = pathWithSearch;
      }
    }
  } catch {
    /* SSR / URL parse failure — best-effort */
  }
  // Best-effort signout — we don't care if it 401s (there's often
  // nothing to sign out of, that's the point). Do the reload in
  // .finally() so we don't wait if signout hangs.
  void fetch('/api/signout', {
    method: 'POST',
    credentials: 'include',
  })
    .catch(() => {
      /* best-effort */
    })
    .finally(() => {
      const params = new URLSearchParams();
      params.set('_r', String(Date.now()));
      params.set('reset', '1');
      if (nextPath !== null) {
        params.set('next', nextPath);
      }
      window.location.href = `/sign-in?${params.toString()}`;
    });
}

/**
 * Sub-agent HHHHH (2026-07-29) — read session-scoped auto-reset count.
 * Callers use this to check whether they've already burned the auto-
 * recover budget for the current session (cap = 2). Beyond the cap,
 * SignIn.tsx falls back to the manual retry callout.
 */
export function getSignInResetCount(): number {
  try {
    const n = Number(window.sessionStorage.getItem('olytic:signin:reset_count') ?? '0');
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

/**
 * Sub-agent HHHHH — clear the auto-reset budget on a successful sign-in.
 * Called from SignIn.tsx after the happy path resolves so a subsequent
 * bad-session event later in the same tab gets the full 2-attempt
 * budget again.
 */
export function clearSignInResetCount(): void {
  try {
    window.sessionStorage.removeItem('olytic:signin:reset_count');
  } catch {
    /* noop */
  }
}

export async function signIn(
  email: string,
  password: string,
): Promise<{ ok: true; user?: CurrentUser }> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), SIGNIN_TIMEOUT_MS);
  // Sub-agent HHHHH — stamp an in-progress marker so a subsequent
  // SignIn mount (after a hard reload) can detect + wipe leftover
  // state if this attempt gets abandoned mid-flight (tab closed,
  // navigation, etc.).
  try {
    window.sessionStorage.setItem('olytic:signin:started_at', String(Date.now()));
  } catch {
    /* noop */
  }
  let res: Response;
  try {
    res = await fetch('/api/signin', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timeoutId);
    // Abort → user-facing timeout envelope. Anything else is network-
    // level (offline, DNS, TLS) — same envelope shape, different copy.
    // We intentionally leave the started_at marker in place — the
    // caller's auto-recover path is about to hard-reload, and the
    // marker's stale timestamp will get wiped by the mount-time
    // sweep on the reloaded /sign-in page.
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ApiError(
        0,
        "Sign-in is taking longer than expected. Try again — if it keeps happening, refresh the page or contact support.",
        `POST /api/signin exceeded ${SIGNIN_TIMEOUT_MS}ms`,
        null,
        'signin_timeout',
      );
    }
    const detail = err instanceof Error ? err.message : String(err);
    throw new ApiError(
      0,
      "We couldn't reach Olytic. Check your connection and try again.",
      detail,
      null,
      'network_failure',
    );
  }
  clearTimeout(timeoutId);

  const text = await res.text();
  let parsed: unknown = null;
  if (text !== '') {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }
  if (!res.ok) {
    const { message, detail, incidentId, code } = extractError(
      text,
      '/api/signin',
      'POST',
      res.status,
    );
    throw new ApiError(res.status, message, detail, incidentId, code);
  }
  // Sub-agent HHHHH — happy path. Clear both the in-progress marker
  // AND the auto-recover budget so a stale-session event later in
  // the same session gets the full 2-attempt reset budget back.
  try {
    window.sessionStorage.removeItem('olytic:signin:started_at');
    window.sessionStorage.removeItem('olytic:signin:reset_count');
  } catch {
    /* noop */
  }
  return parsed as { ok: true; user?: CurrentUser };
}

export async function signUp(
  email: string,
  password: string,
  name: string,
  companyName?: string,
): Promise<{ ok: true; user?: CurrentUser }> {
  // Sub-agent NO-ABORT (2026-08-13) — mirrors the signIn AbortController
  // envelope so signup gets a bounded 90s belt with a friendly timeout
  // envelope instead of spinning indefinitely on a stuck edge. See
  // SIGNUP_TIMEOUT_MS docs for the "never abort before 60s" rationale.
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), SIGNUP_TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch('/api/signup', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        password,
        name,
        ...(companyName !== undefined && companyName !== '' ? { companyName } : {}),
      }),
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timeoutId);
    if (err instanceof DOMException && err.name === 'AbortError') {
      // signup_timeout — SignUp.tsx branches on err.code to render the
      // recovery UI (retry + "already have an account?" link) rather
      // than a bare toast. Idempotent-retry safe: a subsequent submit
      // either succeeds fresh (STEP 1 orphan cleanup path) or lands on
      // the 409 email_taken callout which offers Sign In / Reset links.
      throw new ApiError(
        0,
        "Signup is taking longer than expected. If your account was created, you can sign in with the email you entered — or retry.",
        `POST /api/signup exceeded ${SIGNUP_TIMEOUT_MS}ms`,
        null,
        'signup_timeout',
      );
    }
    const detail = err instanceof Error ? err.message : String(err);
    throw new ApiError(
      0,
      "We couldn't reach Olytic. Check your connection and try again.",
      detail,
      null,
      'network_failure',
    );
  }
  clearTimeout(timeoutId);
  const text = await res.text();
  let parsed: unknown = null;
  if (text !== '') {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }
  if (!res.ok) {
    const { message, detail, incidentId, code } = extractError(
      text,
      '/api/signup',
      'POST',
      res.status,
    );
    throw new ApiError(res.status, message, detail, incidentId, code);
  }
  return parsed as { ok: true; user?: CurrentUser };
}

export function forgotPassword(email: string): Promise<{ ok: true }> {
  return post('/api/forgot-password', { email });
}

export function resetPassword(
  token: string,
  newPassword: string,
  /**
   * Sub-agent AUTONOMOUS-CLOSE (2026-08-02) — Fix A. Optional email hint
   * from the reset-flow URL (`/reset-password?token=...&email=...`).
   * When present, the server-side endpoint looks up the userId by email
   * so it can revoke every existing session (kick every device off).
   * When absent, session revocation degrades to "sessions expire at TTL"
   * and the server logs a warning.
   */
  emailHint?: string,
): Promise<{ ok: true }> {
  return post('/api/reset-password', { token, newPassword, emailHint });
}

export function sendVerificationEmail(): Promise<{ ok: true }> {
  return post('/api/send-verification-email', {});
}

export function verifyEmail(token: string): Promise<{ ok: true }> {
  return post('/api/verify-email', { token });
}

export function verifyEmailOtp(otp: string): Promise<{ ok: true }> {
  return post('/api/verify-email-otp', { otp });
}

export function signOut(): Promise<{ ok: true }> {
  return post('/api/signout', {});
}
